import apiClient from "@/helpers/api.helper";
import { ENQUIRY_TRACK_BY_EMAIL_DOB, ENQUIRY_OTP_SEND, ENQUIRY_OTP_VERIFY } from "@/constants/urls";
import { TrackApplicationResponse } from "@/models/api/trackApplication";

const extractErrorMessage = (error: any, fallback: string): string =>
  error?.details?.message ||
  error?.details?.error ||
  (typeof error?.details === 'string' ? error.details : null) ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

/**
 * Service for tracking application status
 * Requires an OTP verification token obtained via sendTrackApplicationOtp + verifyTrackApplicationOtp
 */
export const trackApplicationStatus = async (
  studentEmail: string,
  dateOfBirth: string,
  verificationToken: string
): Promise<TrackApplicationResponse> => {
  try {
    // Sent as a POST body rather than URL path segments - an email containing "+" (e.g. Gmail's
    // +tag addressing) breaks server-side route matching even when URL-encoded on this end.
    return await apiClient.post<TrackApplicationResponse>(ENQUIRY_TRACK_BY_EMAIL_DOB, {
      studentEmail: studentEmail.trim(),
      dateOfBirth,
      verificationToken,
    });
  } catch (error: any) {
    throw new Error(extractErrorMessage(error, "Failed to fetch application status. Please verify your email and date of birth."));
  }
};

export interface OtpActionResult {
  message: string;
}

export interface OtpVerifyResult {
  success: boolean;
  message: string;
  verificationToken?: string;
  expiresAt?: string;
}

/**
 * Sends a one-time verification code to the email on file for the application
 * matching this email + date of birth.
 */
export const sendTrackApplicationOtp = async (
  studentEmail: string,
  dateOfBirthApi: string
): Promise<OtpActionResult> => {
  try {
    return await apiClient.post<OtpActionResult>(ENQUIRY_OTP_SEND, {
      studentEmail,
      dateOfBirth: dateOfBirthApi,
    });
  } catch (error: any) {
    throw new Error(extractErrorMessage(error, "Failed to send OTP. Please try again."));
  }
};

/**
 * Verifies the OTP code and returns a short-lived verification token to use with trackApplicationStatus.
 */
export const verifyTrackApplicationOtp = async (
  studentEmail: string,
  dateOfBirthApi: string,
  otpCode: string
): Promise<OtpVerifyResult> => {
  try {
    return await apiClient.post<OtpVerifyResult>(ENQUIRY_OTP_VERIFY, {
      studentEmail,
      dateOfBirth: dateOfBirthApi,
      otpCode,
    });
  } catch (error: any) {
    throw new Error(extractErrorMessage(error, "Failed to verify OTP. Please try again."));
  }
};

/**
 * Helper function to format date from MM/DD/YYYY to YYYY-MM-DD
 */
export const formatDateForApi = (formattedDate: string): string => {
  // formattedDate is in format "MM / DD / YYYY"
  const parts = formattedDate.replace(/\s/g, "").split("/");
  if (parts.length !== 3) {
    throw new Error("Invalid date format");
  }

  const [month, day, year] = parts;
  return `${year}-${month}-${day}`;
};

/**
 * Helper function to format date from YYYY-MM-DD to MM/DD/YYYY
 */
export const formatDateForDisplay = (dateString: string): string => {
  const [year, month, day] = dateString.split("-");
  return `${month} / ${day} / ${year}`;
};
